import React from 'react'
import { ZoomIn, ZoomOut, Layers, Circle, Flame } from 'lucide-react'
import { LayerConfig, VisualizationType } from '../../../types/map'

interface MapControlsProps {
  zoom: number
  setZoom: (z: number) => void
  layers: LayerConfig[]
  setLayers: React.Dispatch<React.SetStateAction<LayerConfig[]>>
  visualizationType: VisualizationType
  onVisualizationTypeChange: (type: VisualizationType) => void
}

const MapControls: React.FC<MapControlsProps> = ({ 
  zoom, 
  setZoom, 
  layers, 
  setLayers, 
  visualizationType, 
  onVisualizationTypeChange 
}) => {
  // Find the coffee shop layer state
  const coffeeLayer = layers.find(l => l.name === 'Coffee Shops')
  const coffeeVisible = coffeeLayer?.enabled

  // Handler to toggle coffee shop layer
  const toggleCoffeeLayer = () => {
    setLayers(prevLayers =>
      prevLayers.map(layer =>
        layer.name === 'Coffee Shops'
          ? { ...layer, enabled: !layer.enabled }
          : layer
      )
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border border-neutral-200 p-2">
      <div className="flex flex-col space-y-2">
        {/* Zoom Controls */}
        <button
          className="p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded transition-colors"
          onClick={() => {
            const newZoom = zoom + 1
            if (newZoom <= 22) { // Maximum zoom level
              setZoom(newZoom)
            }
          }}
          aria-label="Zoom in"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          className="p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded transition-colors"
          onClick={() => {
            const newZoom = zoom - 1
            if (newZoom >= 8) { // Minimum zoom level
              setZoom(newZoom)
            }
          }}
          aria-label="Zoom out"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        
        {/* Visualization Type Toggle */}
        <div className="border-t border-neutral-200 pt-2">
          <div className="flex flex-col space-y-1">
            <button
              className={`p-2 rounded transition-colors ${
                visualizationType === 'scatter'
                  ? 'text-coffee-600 bg-coffee-50 hover:bg-coffee-100'
                  : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
              }`}
              onClick={() => onVisualizationTypeChange('scatter')}
              aria-label="Switch to scatter plot"
            >
              <Circle className="w-4 h-4" />
            </button>
            <button
              className={`p-2 rounded transition-colors ${
                visualizationType === 'heatmap'
                  ? 'text-orange-600 bg-orange-50 hover:bg-orange-100'
                  : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
              }`}
              onClick={() => onVisualizationTypeChange('heatmap')}
              aria-label="Switch to heatmap"
            >
              <Flame className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Layer Toggle */}
        <div className="border-t border-neutral-200 pt-2">
          <button
            className={`p-2 rounded transition-colors ${
              coffeeVisible
                ? 'text-coffee-600 bg-coffee-50 hover:bg-coffee-100'
                : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
            }`}
            onClick={toggleCoffeeLayer}
            aria-label={coffeeVisible ? 'Hide coffee shops' : 'Show coffee shops'}
          >
            <Layers className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default MapControls 